import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, APP_INITIALIZER, NgModule, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as fromUser from '@titan/devkit/user';
import { UserCookieService, UserCookieModule } from '@titan/devkit/user';
import * as i1 from '@ngx-translate/core';
import { TranslateLoader, TranslateService, TranslateModule } from '@ngx-translate/core';
import { TitanConfigService } from '@titan/devkit/config';
import { first, filter, mergeMap, map, catchError, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as i2 from '@ngrx/effects';
import { Actions, createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { createAction, props } from '@ngrx/store';
import * as i1$1 from '@titan/devkit/http';
import { configureHttpRootProviders } from '@titan/devkit/http';
import { ZeusService } from '@titan/devkit/zeus';
import { DOCUMENT } from '@angular/common';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Title } from '@angular/platform-browser';
import { TitleStrategy } from '@angular/router';

/**
 * Factory provider used to dynamically get configuration data at runtime on app initialization.
 *
 * @param bootstrapService Service that makes HTTP Request for bootstrap information
 * @param configService Config Service that will store the HTTP Response bootstrap information
 * @param standaloneApp Is the App Standalone? (a typical Titan App that is not the login page)
 * @param appName The app name set, used to determine which app to get bootstrap info for
 *
 * @returns A function that returns a promise after getting the bootstrap information.
 *
 * @docs-public
 */
function bootstrapConfigurationFactory(bootstrapService, configService, standaloneApp, appName) {
    return () => {
        return new Promise((resolve, reject) => {
            const observer = {
                next(config) {
                    configService.config = config;
                    resolve(true);
                },
                error(error) {
                    console.error('Error initializing bootstrap: ', error);
                    reject(error);
                },
            };
            if (standaloneApp) {
                bootstrapService.getBootstrapConfig(appName).subscribe(observer);
                return;
            }
            bootstrapService.getBootstrapConfig().subscribe(observer);
        });
    };
}

/**
 * Responsible for initializing the correct translation file before allowing the application to initialize. We prioritize
 * using the locale from the user cookie, then fallback to the browsers language if it's a supported locale. If that
 * locale is not supported, we use the default locale. After determining the locale to fetch, the factory eagerly fetches
 * the locale file needed for the user, after the file has been parsed, we merge the devkit translations.
 *
 * @param userCookie
 * @param translateService
 * @param supportedLocales
 * @param defaultLocale
 *
 * @docs-public
 */
function initializeTranslationsFactory(userCookie, translateService, supportedLocales, defaultLocale) {
    return () => {
        const userCookieLocale = userCookie.getLocale();
        const browserLanguage = translateService.getBrowserLang();
        const useBrowser = browserLanguage && supportedLocales.includes(browserLanguage);
        const useCookie = userCookieLocale && supportedLocales.includes(userCookieLocale);
        const locale = (useCookie && userCookieLocale) || (useBrowser && browserLanguage) || defaultLocale;
        return new Promise((resolve, reject) => {
            translateService.onDefaultLangChange.pipe(first()).subscribe(() => {
                resolve(true);
            });
            translateService
                .use(locale)
                .pipe(first())
                .subscribe({
                next: () => { },
                error: error => {
                    console.error('Failed to get translation: ', locale);
                    reject(error);
                },
            });
        });
    };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/* COPIED FROM
 * https://github.com/ngx-translate/core/blob/76d5a87c9f9a5a484426f5459010ad471370bc6d/projects/ngx-translate/core/src/lib/util.ts
 */
function isDefined(value) {
    return typeof value !== 'undefined' && value !== null;
}
function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}
function mergeDeep(target, source) {
    const output = Object.assign({}, target);
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach((key) => {
            if (isObject(source[key])) {
                if (!(key in target)) {
                    Object.assign(output, { [key]: source[key] });
                }
                else {
                    output[key] = mergeDeep(target[key], source[key]);
                }
            }
            else {
                Object.assign(output, { [key]: source[key] });
            }
        });
    }
    return output;
}

/* eslint-disable max-len */
const en = {
    HELP_MENU: {
        TITAN_SUPPORT: 'Titan Support',
        FEEDBACK: 'Submit Feedback',
        HELP_AND_FDBK: 'HELP AND FEEDBACK',
        FEEDBACK_HEADER: 'Submit Feedback',
        FEEDBACK_SUBHEADER: "Give us feedback on anything you'd like!",
        FORM_NAME: 'Name (Optional)',
        FORM_NAME_ERROR: 'Name is required to respond to feedback',
        FORM_FDBK_FOR: 'Feedback For:',
        COMMENTS: 'Comments',
        FEATURE_REQ: 'Feature Requests',
        ISSUES_FOUND: 'Issues Found',
        GENERAL: 'General',
        FDBK_REQ_ERROR: 'Feedback is required',
        ALLOW_CONTACT: 'Allow us to contact you regarding this feedback',
        SUBMIT: 'Submit',
    },
    APP_SHELL: {
        FLYOUT_MENU_CLOSE: 'Close Menu',
        HEADERS: {
            PINNED_APPLICATIONS: 'Pinned Applications',
            RECENT_APPLICATIONS: 'Recent Applications',
        },
        THEME: 'Theme',
        USE_LEGACY_TITAN: 'Use Legacy Titan',
        TUTORIAL_MESSAGE: 'If you need to switch back to Legacy Titan, you can do so in the menu here',
        SWITCH_THEME: 'Switch theme',
        LOGOUT: 'Log Out',
        PAGES: 'Pages',
        INFO: 'Info',
        NO_RECENTLY_VIEWED_PAGES: 'No recently viewed pages',
        NAV_LINKS: {
            BROWSE: 'Browse',
            BROWSE_DASHBOARDS: 'Browse Dashboards',
            BROWSE_APPLICATIONS: 'Browse Applications',
            MANAGE_DASHBOARDS: 'Manage Dashboards',
            RELEASES: 'Release Notes',
            RECENTLY_VIEWED: 'Recently Viewed',
            BACK_TO_TITAN: 'Back to Titan',
        },
        NAV_HINTS: {
            BROWSE: 'Browse dashboards or apps',
            BROWSE_DASHBOARDS: 'Browse all dashboards',
            BROWSE_APPLICATIONS: 'Browse your applications',
            MANAGE_DASHBOARDS: 'Manage your dashboards',
            RELEASES: 'View release notes',
            LOGOUT: 'Logout of Titan',
            BACK_TO_TITAN: 'Back to Titan',
        },
        STANDALONE_WELCOME: '{{ appTitle }} is now a fullscreen application! It will no longer be available on dashboards. Feel free to bookmark this page.',
        SERVICE_UNAVAILABLE: '{{ appTitle }} is unavailable due to a service being unavailable.',
        SERVICES_UNAVAILABLE: '{{ appTitle }} is unavailable due to {{ count }} services being unavailable.',
        SERVICES_UNAVAILABLE_MESSAGE: 'If this problem persists, please email the following debug information to {{ contactAddress }}:',
        ZONE_SELECT: 'Select your zones',
        ALL_ZONES: 'All Zones',
        DASHBOARD_TOOLBAR: {
            SETTINGS: {
                EDIT: 'Edit',
                SHARE: 'Share',
                CLONE: 'Clone',
                DELETE: 'Delete',
            },
            NO_PERMISSION_MESSAGE: 'You do not have permission to edit this dashboard.',
        },
        APP_INFO: {
            OK: 'Ok',
            APPLICATION_INFO: 'Application Information',
            VERSIONS: 'VERSIONS',
            APPLICATION: 'Application',
            ZEUS: 'Zeus',
            STATUS: 'Status',
            LAST_MODIFIED_DATE: 'Last Modified Date',
            LAST_MODIFIED_BY: 'Last Modified By',
        },
        INFO_PANEL: {
            SUPPORT_MODAL_TITLE: 'Titan Support',
            SUPPORT_MODAL_CONFLUENCE_TITLE: 'Confluence - Help Topics',
            EMAIL: 'Email',
        },
    },
};

const jp = {
    HELP_MENU: {
        TITAN_SUPPORT: 'Titan サポート',
        FEEDBACK: 'フィードバックを送信',
        HELP_AND_FDBK: 'ヘルプとフィードバック',
        FEEDBACK_HEADER: 'フィードバックを送信',
        FEEDBACK_SUBHEADER: 'フィードバックをお寄せください',
        FORM_NAME: '名前 (オプション)',
        FORM_NAME_ERROR: 'フィードバックに返信するには名前が必要です',
        FORM_FDBK_FOR: 'フィードバック:',
        COMMENTS: 'コメント',
        FEATURE_REQ: '機能リクエスト',
        ISSUES_FOUND: '問題が見つかりました',
        GENERAL: '全般的',
        FDBK_REQ_ERROR: 'フィードバックが必要です',
        ALLOW_CONTACT: 'このフィードバックに関して、私たちがあなたに連絡できるようにします',
        SUBMIT: '提出する',
    },
    APP_SHELL: {
        FLYOUT_MENU_CLOSE: 'メニューを閉じる',
        HEADERS: {
            PINNED_APPLICATIONS: '固定されたアプリケーション',
            RECENT_APPLICATIONS: '最近のアプリケーション',
        },
        THEME: 'テーマ',
        USE_LEGACY_TITAN: 'レガシー タイタンを使用',
        TUTORIAL_MESSAGE: '旧Titanへは、こちらのMenuから戻れます',
        INFO: '情報',
        SWITCH_THEME: 'テーマを切り替える',
        LOGOUT: 'ログアウト',
        PAGES: 'ページ数',
        NO_RECENTLY_VIEWED_PAGES: '最近閲覧したページはありません',
        NAV_LINKS: {
            BROWSE: 'ブラウズ',
            BROWSE_DASHBOARDS: 'ダッシュボードの参照',
            BROWSE_APPLICATIONS: 'アプリケーションを閲覧する',
            MANAGE_DASHBOARDS: 'ダッシュボードの管理',
            RELEASES: 'リリース',
            RECENTLY_VIEWED: '最近閲覧した',
            BACK_TO_TITAN: 'タイタンに戻る',
        },
        NAV_HINTS: {
            BROWSE: 'ダッシュボードかアプリを閲覧',
            BROWSE_DASHBOARDS: 'ダッシュボード全部を閲覧',
            BROWSE_APPLICATIONS: 'アプリケーションを閲覧する',
            MANAGE_DASHBOARDS: 'ダッシュボードを取り締まる',
            RELEASES: 'リリース詳細を見る',
            LOGOUT: 'ログアウト Titan',
            BACK_TO_TITAN: 'タイタンに戻る',
        },
        STANDALONE_WELCOME: '{{ appTitle }} はフルスクリーンアプリケーションになりました！ ダッシュボードでは使用できなくなります。 このページをブックマークしてください。',
        SERVICE_UNAVAILABLE: '{{ appTitle }} はサービスが利用できないため利用できません',
        SERVICES_UNAVAILABLE: '{{ appTitle }} は{{ count }}つのサービスが利用できないため利用できません',
        SERVICES_UNAVAILABLE_MESSAGE: 'この問題が引き続く場合は、{{contactAddress}} に次のデバッグ情報をメールしてください。',
        ZONE_SELECT: 'あなたのゾーンを選択してください',
        ALL_ZONES: '全ゾーン',
        DASHBOARD_TOOLBAR: {
            SETTINGS: {
                EDIT: 'Edit',
                SHARE: 'Share',
                CLONE: 'Clone',
                DELETE: 'Delete',
            },
            NO_PERMISSION_MESSAGE: 'このダッシュボードを編集する権限がありません。',
        },
        APP_INFO: {
            OK: 'Ok',
            APPLICATION_INFO: 'Application Information',
            VERSIONS: 'VERSIONS',
            APPLICATION: 'Application',
            ZEUS: 'Zeus',
            STATUS: 'Status',
            LAST_MODIFIED_DATE: 'Last Modified Date',
            LAST_MODIFIED_BY: 'Last Modified By',
        },
        INFO_PANEL: {
            SUPPORT_MODAL_TITLE: 'Titanサポート',
            SUPPORT_MODAL_CONFLUENCE_TITLE: 'Confluence - ヘルプトピックス',
            EMAIL: 'Eメール',
        },
    },
};

const translationDefinitions = { en, jp };

/** @docs-public */
class TitanDevkitTranslationLoader extends TranslateLoader {
    constructor(http, configService) {
        super();
        this.http = http;
        this.configService = configService;
    }
    /**
     * Gets the translations from the server
     *
     * @docs-public
     */
    getTranslation(lang) {
        return this.configService.configInit$.pipe(filter(r => !!r), mergeMap(() => {
            if (this.configService.app === undefined) {
                const gaiaPrefix = `${this.configService.gaiaUrlPath === '/' ? '' : this.configService.gaiaUrlPath}/dist/${this.configService.versions.entry}/assets/i18n/`;
                return this.http.get(`${gaiaPrefix}${lang}.json`).pipe(map(response => {
                    const translations = translationDefinitions[lang] || {};
                    return mergeDeep(response, translations);
                }), catchError(err => {
                    console.error('Failed to get translation: ', lang);
                    console.error(err);
                    const translations = translationDefinitions[lang] || {};
                    return of(translations);
                }));
            }
            const name = this.configService.app.name.replace(/^app-/, '');
            const prefix = `/app/${name}/dist/${this.configService.app.version}/assets/i18n/`;
            return this.http.get(`${prefix}${lang}.json`).pipe(map(response => {
                const translations = translationDefinitions[lang] || {};
                return mergeDeep(response, translations);
            }), catchError(err => {
                console.error('Failed to get translation: ', lang);
                console.error(err);
                const translations = translationDefinitions[lang] || {};
                return of(translations);
            }));
        }));
    }
}

/** @docs-public */
function translateLoaderFactory(http, configService) {
    return new TitanDevkitTranslationLoader(http, configService);
}

/**
 * An injection token used to get the app name. This is set within the Devkit Config.
 *
 * @docs-public
 */
const APP_NAME = new InjectionToken('Application Name');
/**
 * An injection token used to get the app title. This is set within the Devkit Config.
 *
 * @docs-public
 */
const APP_TITLE = new InjectionToken('Application Title');
const SUPPORTED_LOCALES = new InjectionToken('Supported Locales');
const DEFAULT_LOCALE = new InjectionToken('Default Locale');
const STANDALONE_APP = new InjectionToken('Standalone App');
const CONTACT_ADDRESS = new InjectionToken('Contact Address');
const ATLAS_MODE = new InjectionToken('Atlas Mode');
const APP_PACKAGES = new InjectionToken('Application Packages');
const PREVIEW_MODE = new InjectionToken('Preview Mode');
/**
 * Configuration for devkit
 *
 * @docs-public
 */
const DEVKIT_CONFIG = new InjectionToken('Devkit Config');

class BootstrapService {
    constructor() {
        this.httpService = inject(HttpClient);
    }
    getBootstrapConfig(appName) {
        return this.httpService.get('/bootstrap', {
            params: appName ? { app: appName } : undefined,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: BootstrapService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: BootstrapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: BootstrapService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class BootstrapModule {
    static forRoot() {
        return {
            ngModule: BootstrapModule,
            providers: [
                {
                    provide: APP_INITIALIZER,
                    useFactory: bootstrapConfigurationFactory,
                    multi: true,
                    deps: [BootstrapService, TitanConfigService, STANDALONE_APP, APP_NAME],
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeTranslationsFactory,
                    multi: true,
                    deps: [UserCookieService, TranslateService, SUPPORTED_LOCALES, DEFAULT_LOCALE],
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: BootstrapModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: BootstrapModule, imports: [i1.TranslateModule, UserCookieModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: BootstrapModule, imports: [TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: translateLoaderFactory,
                    deps: [HttpClient, TitanConfigService],
                },
            }),
            UserCookieModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: BootstrapModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        TranslateModule.forRoot({
                            loader: {
                                provide: TranslateLoader,
                                useFactory: translateLoaderFactory,
                                deps: [HttpClient, TitanConfigService],
                            },
                        }),
                        UserCookieModule,
                    ],
                }]
        }] });

/* eslint-disable @typescript-eslint/prefer-as-const */
const getZonesSuccess = createAction('[Bootstrap] Get Zones Success', props());
const getZonesFailed = createAction('[Bootstrap] Get Zones Failed', props());

/** @docs-public */
class ZoneService {
    constructor() {
        this.httpService = inject(HttpClient);
    }
    getZones() {
        return this.httpService.get('/zones');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ZoneEffects {
    constructor() {
        this.actions$ = inject(Actions);
        this.zoneService = inject(ZoneService);
        this.config = inject(TitanConfigService);
        this.getZonesOnAuthenticationSuccess$ = createEffect(() => this.actions$.pipe(ofType(fromUser.authenticateSuccess), switchMap(() => this.zoneService.getZones().pipe(map(zones => getZonesSuccess({ zones })), catchError(error => of(getZonesFailed({ error })))))));
        this.setZonesInConfigService$ = createEffect(() => this.actions$.pipe(ofType(getZonesSuccess), tap(({ zones }) => (this.config.zones = zones)), first() // we only need to do this one time, no need to keep it around.
        ), { dispatch: false });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneEffects }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneEffects, decorators: [{
            type: Injectable
        }] });

/** @docs-public */
const locales = ['en', 'jp'];
const defaultLocale = 'en';
const defaultContactAddress = 'sie-ftg-titan-platform@sony.com';
const psn_environments = ['np', 'e1-np', 'l1-np', 'mgmt', 'dev', 'prod-qa', 'sp-int'];

class LocaleEffects {
    constructor() {
        this.actions$ = inject(Actions);
        this.translateService = inject(TranslateService);
        this.useLocale$ = createEffect(() => this.actions$.pipe(ofType(fromUser.setLocale), map(({ payload }) => payload), switchMap(locale => this.translateService.use(locale))), { dispatch: false });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocaleEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocaleEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocaleEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AppRecentlyViewedEffects {
    constructor() {
        this.appName = inject(APP_NAME);
        this.actions$ = inject(Actions);
        this.zeusService = inject(ZeusService);
        this.setRecentlyViewedApp$ = createEffect(() => this.actions$.pipe(ofType(fromUser.getAdsUserSuccess), switchMap(() => this.zeusService.getApplications()), map((apps) => apps.find(({ id }) => id === this.appName)), filter(r => !!r), map(app => fromUser.setRecentlyViewedApplication({ payload: app })), first()));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AppRecentlyViewedEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AppRecentlyViewedEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AppRecentlyViewedEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const generateRedirectLoginUrl = ({ origin, pathname, queryParamsString, gaiaUrlPath, }) => {
    const redirectPath = `${gaiaUrlPath === '/' ? '' : gaiaUrlPath}/#/login`;
    const redirectParams = genRedirectLoginParams({ pathname, queryParamsString });
    const url = new URL(redirectPath, origin).href;
    return `${url}${redirectParams}`;
};
const genRedirectLoginParams = ({ pathname, queryParamsString }) => {
    const appUrlParam = `appUrl=${pathname}`;
    const restOfParams = queryParamsString ? `&${queryParamsString}` : ``;
    return `?${appUrlParam}${restOfParams}`;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
class AtlasDevkitAppAuthEffects {
    constructor() {
        this.actions$ = inject(Actions);
        this.document = inject(DOCUMENT);
        this.atlasMode = inject(ATLAS_MODE);
        this.titanConfigService = inject(TitanConfigService);
        this.appLogoutSuccess$ = createEffect(() => this.actions$.pipe(ofType(fromUser.logoutSuccess), filter(() => this.atlasMode === false), tap(() => {
            const { origin, pathname, search } = this.document.location;
            const gaiaUrlPath = this.titanConfigService.gaiaUrlPath;
            const queryParamsString = new URLSearchParams(search).toString();
            const redirectUrl = generateRedirectLoginUrl({ origin, pathname, queryParamsString, gaiaUrlPath });
            this.document.location.replace(redirectUrl);
        })), { dispatch: false });
        this.appAuthCheckFailure$ = createEffect(() => this.actions$.pipe(ofType(fromUser.authenticateFailed), filter(() => this.atlasMode === false), tap(() => {
            const { origin, pathname, search } = this.document.location;
            const gaiaUrlPath = this.titanConfigService.gaiaUrlPath;
            const queryParamsString = new URLSearchParams(search).toString();
            const redirectUrl = generateRedirectLoginUrl({ origin, pathname, queryParamsString, gaiaUrlPath });
            this.document.location.replace(redirectUrl);
        })), { dispatch: false });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasDevkitAppAuthEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasDevkitAppAuthEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasDevkitAppAuthEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class TitanDevkitCoreRootMoule {
    constructor(atlasHttpClientModule) {
        if (atlasHttpClientModule) {
            throw new Error('AtlasDevkitModule provides AtlasHttpClientModule functionality, please remove it.');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreRootMoule, deps: [{ token: i1$1.AtlasHttpClientModule, optional: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreRootMoule, imports: [i2.EffectsFeatureModule, UserCookieModule, i1.TranslateModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreRootMoule, imports: [EffectsModule.forFeature([
                // For performing translations when different language selected
                LocaleEffects,
                // Effects to update recently viewed applications per user
                AppRecentlyViewedEffects,
                // Shared authentication effects for titan applications
                AtlasDevkitAppAuthEffects,
                // For getting available zones
                ZoneEffects,
            ]),
            UserCookieModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: translateLoaderFactory,
                    deps: [HttpClient, TitanConfigService],
                },
            })] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreRootMoule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        EffectsModule.forFeature([
                            // For performing translations when different language selected
                            LocaleEffects,
                            // Effects to update recently viewed applications per user
                            AppRecentlyViewedEffects,
                            // Shared authentication effects for titan applications
                            AtlasDevkitAppAuthEffects,
                            // For getting available zones
                            ZoneEffects,
                        ]),
                        UserCookieModule,
                        TranslateModule.forRoot({
                            loader: {
                                provide: TranslateLoader,
                                useFactory: translateLoaderFactory,
                                deps: [HttpClient, TitanConfigService],
                            },
                        }),
                    ],
                }]
        }], ctorParameters: () => [{ type: i1$1.AtlasHttpClientModule, decorators: [{
                    type: Optional
                }] }] });
/**
 * Sets up shared providers that are consumed by Titan App Shell.
 * The providers initialize a Titan app for dynamic config support, internationalization support,
 * and sets up HTTP Interceptors to attach auth token to HTTP request headers and attach zone information to HTTP request headers *
 *
 * @docs-public
 *
 * @docs-primary-export
 */
class TitanDevkitCoreModule {
    static forRoot(config) {
        return {
            ngModule: TitanDevkitCoreRootMoule,
            providers: [
                {
                    provide: DEVKIT_CONFIG,
                    useValue: config,
                },
                {
                    provide: APP_NAME,
                    useValue: config.appName,
                },
                {
                    provide: APP_TITLE,
                    useValue: config.title,
                },
                {
                    provide: STANDALONE_APP,
                    useValue: config.standaloneApp,
                },
                {
                    provide: CONTACT_ADDRESS,
                    useValue: config.contactAddress || defaultContactAddress,
                },
                {
                    provide: APP_PACKAGES,
                    useValue: config.appPackages,
                },
                {
                    provide: ATLAS_MODE,
                    useValue: config.standaloneApp === false,
                },
                {
                    provide: SUPPORTED_LOCALES,
                    useValue: config.locales || locales,
                },
                {
                    provide: DEFAULT_LOCALE,
                    useValue: config.defaultLocale || defaultLocale,
                },
                {
                    provide: PREVIEW_MODE,
                    useValue: !!config.previewMode,
                },
                ...configureHttpRootProviders(config.appName),
                {
                    provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
                    useValue: { appearance: 'outline' },
                },
                // App initializers, dynamic configuration, translation fetching...
                {
                    provide: APP_INITIALIZER,
                    useFactory: config.bootstrapFactory ? config.bootstrapFactory.useFactory : bootstrapConfigurationFactory,
                    multi: true,
                    deps: config.bootstrapFactory
                        ? config.bootstrapFactory.deps
                        : [BootstrapService, TitanConfigService, STANDALONE_APP, APP_NAME],
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeTranslationsFactory,
                    multi: true,
                    deps: [UserCookieService, TranslateService, SUPPORTED_LOCALES, DEFAULT_LOCALE],
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanDevkitCoreModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/**
 * @description
 * Updates the browser tab's title, appending the current zone Titan is in with the title.
 *
 * For example, if the app name was TREX, and the zone was AIT1, and the title set for the
 * page is Home, the title will be `HOME | AIT1 | TREX`
 *
 * @docs-public
 */
class TitanTitleStrategy extends TitleStrategy {
    constructor() {
        super();
        this.title = inject(Title);
        this.config = inject(TitanConfigService);
        this.appOriginalTitle = inject(APP_TITLE);
    }
    updateTitle(routerState) {
        const routeTitle = this.buildTitle(routerState);
        const environment = this.config.environment.toUpperCase();
        if (!routeTitle) {
            this.title.setTitle(`${environment} | ${this.appOriginalTitle}`);
        }
        else {
            this.title.setTitle(`${routeTitle} | ${environment} | ${this.appOriginalTitle}`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanTitleStrategy, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanTitleStrategy, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanTitleStrategy, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * Generated bundle index. Do not edit.
 */

export { APP_NAME, APP_PACKAGES, APP_TITLE, ATLAS_MODE, BootstrapModule, BootstrapService, CONTACT_ADDRESS, DEFAULT_LOCALE, DEVKIT_CONFIG, PREVIEW_MODE, STANDALONE_APP, SUPPORTED_LOCALES, TitanDevkitCoreModule, TitanDevkitCoreRootMoule, TitanDevkitTranslationLoader, TitanTitleStrategy, ZoneEffects, ZoneService, bootstrapConfigurationFactory, defaultContactAddress, defaultLocale, initializeTranslationsFactory, locales, psn_environments, translateLoaderFactory, translationDefinitions };
